import { useState } from "react";
import { useUrlFilters } from "../../shared/hooks/urlFilters";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import { useAuth } from "../../app/AuthContext";
import { api, ApiError } from "../../shared/api/client";
import type {
  ModuleReach,
  ModuleTypeView,
  ModuleView,
  PageResponse,
} from "../../shared/types/api";
import {
  Button,
  DataTable,
  Drawer,
  EmptyState,
  Field,
  FilterBar,
  InlineAlert,
  Input,
  LoadingState,
  PageHeader,
  Select,
  StatusBadge,
  type Column,
} from "../../shared/ui";
import { ModuleDrawer } from "./ModuleDrawer";

/**
 * The catalogue of modules a product is built from.
 *
 * <p>Top level rather than under a project, because a standalone module is not owned by one:
 * Accounting exists whether or not PR Blocks uses it, and three projects using it are three links
 * to one row rather than three copies that drift apart the first time somebody corrects a name.
 *
 * <p>Withdrawn modules stay listed. Every past release names the modules it shipped, and "which
 * version of Inventory did 5.0 ship?" is exactly the question people ask about software that has
 * since been retired.
 */
/** Ten a page to begin with, fetched a page at a time — the catalogue grows with the product.
 * The reader can ask for more, and the number they chose rides in the URL with the filters. */
const DEFAULT_PAGE_SIZE = "10";

/* Outside the component so its identity is stable — useUrlFilters memoises on it. */
const MODULE_FILTERS = {
  q: "", moduleTypeId: "", status: "", page: "0", size: DEFAULT_PAGE_SIZE,
};

export function ModulesPage() {
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<ModuleView | null>(null);
  const [adding, setAdding] = useState(false);
  const [reachOf, setReachOf] = useState<ModuleView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  /* In the URL, so a narrowed catalogue survives opening a module and can be sent to somebody. */
  const [filters, setFilters] = useUrlFilters(MODULE_FILTERS);
  const { q: search, moduleTypeId, status } = filters;
  const page = Number(filters.page) || 0;
  const pageSize = Number(filters.size) || Number(DEFAULT_PAGE_SIZE);
  /* Any filter change returns to the first page: page three of a wider result is empty under a
     narrower one, and an empty table reads as "nothing matches" rather than "wrong page". */
  const refilter = (patch: Partial<typeof MODULE_FILTERS>) =>
    setFilters({ ...patch, page: "0" });

  /* The kinds on offer, for the picker. The catalogue's own rows only show the kinds that
     happen to be on this page, which is not the list somebody wants to filter by. */
  const moduleTypes = useQuery({
    queryKey: ["module-types", "offered"],
    queryFn: () => api.get<ModuleTypeView[]>("/api/v1/module-types"),
  });

  const modules = useQuery({
    queryKey: ["modules", "all", search, moduleTypeId, status, page, pageSize],
    queryFn: () => {
      const params = new URLSearchParams({
        page: String(page),
        size: String(pageSize),
      });
      if (search.trim()) {
        params.set("q", search.trim());
      }
      if (moduleTypeId) {
        params.set("moduleTypeId", moduleTypeId);
      }
      if (status) {
        params.set("active", status === "offered" ? "true" : "false");
      }
      return api.get<PageResponse<ModuleView>>(`/api/v1/modules/all?${params.toString()}`);
    },
  });

  const narrowed = Boolean(search.trim() || moduleTypeId || status);

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ["modules"] });
    void queryClient.invalidateQueries({ queryKey: ["project-modules"] });
  };

  const setOffered = async (module: ModuleView, offered: boolean) => {
    setBusy(module.id);
    setError(null);
    try {
      await api.post(`/api/v1/modules/${module.id}/${offered ? "reinstate" : "withdraw"}`);
      refresh();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "Could not do that.");
    } finally {
      setBusy(null);
    }
  };

  const columns: Column<ModuleView>[] = [
    {
      key: "name",
      header: "Module",
      render: (module) => (
        <div className="min-w-0">
          <p className="truncate font-medium text-ink-900">{module.name}</p>
          <p className="font-mono text-xs text-ink-400">{module.code}</p>
        </div>
      ),
    },
    {
      key: "kind",
      header: "Kind",
      render: (module) => (
        <StatusBadge tone={module.moduleType.sharedVersion ? "violet" : "gray"}>
          {module.moduleType.name}
        </StatusBadge>
      ),
    },
    {
      key: "latestVersion",
      header: "Latest release",
      render: (module) =>
        module.latestVersion ? (
          <span className="tnum font-mono text-sm">{module.latestVersion}</span>
        ) : (
          <span className="text-ink-400">
            {module.moduleType.sharedVersion ? "Never released" : "Per project"}
          </span>
        ),
    },
    {
      key: "description",
      header: "Description",
      render: (module) => module.description ?? <span className="text-ink-400">—</span>,
    },
    {
      key: "active",
      header: "Status",
      render: (module) => (
        <StatusBadge tone={module.active ? "green" : "gray"}>
          {module.active ? "Offered" : "Withdrawn"}
        </StatusBadge>
      ),
    },
    {
      key: "actions",
      header: "",
      render: (module) =>
        can("MODULE_MANAGE") ? (
          <div className="flex justify-end gap-1">
            {module.moduleType.sharedVersion && (
              <Button
                variant="ghost"
                size="sm"
                title="Which projects have this module, and which are behind"
                onClick={(event) => {
                  event.stopPropagation();
                  setReachOf(module);
                }}
              >
                Where it is used
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              loading={busy === module.id}
              onClick={(event) => {
                event.stopPropagation();
                void setOffered(module, !module.active);
              }}
            >
              {module.active ? "Withdraw" : "Offer again"}
            </Button>
          </div>
        ) : null,
    },
  ];

  return (
    <>
      <PageHeader
        title="Modules"
        subtitle="The parts your products are built from. A customisable module belongs to one project; a standalone one is shared, and its version is tracked wherever it is used."
      />

      {/* The button belongs with the filters: everything that acts on this list is then one
          strip rather than a button marooned on a row of its own. */}
      <FilterBar
        actions={
          can("MODULE_MANAGE") && (
            <Button icon={<Plus className="h-4 w-4" />} onClick={() => setAdding(true)}>
              New module
            </Button>
          )
        }
      >
        <div className="min-w-52 flex-1">
          <Field label="Search">
            <Input
              placeholder="Name or code"
              value={search}
              onChange={(event) => refilter({ q: event.target.value })}
            />
          </Field>
        </div>
        <Field label="Kind">
          <Select
            value={moduleTypeId}
            onChange={(event) => refilter({ moduleTypeId: event.target.value })}
          >
            <option value="">Every kind</option>
            {(moduleTypes.data ?? []).map((type) => (
              <option key={type.id} value={type.id}>
                {type.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Status">
          <Select
            value={status}
            onChange={(event) => refilter({ status: event.target.value })}
          >
            <option value="">Offered and withdrawn</option>
            <option value="offered">Offered</option>
            <option value="withdrawn">Withdrawn</option>
          </Select>
        </Field>
      </FilterBar>

      {error && (
        <div className="mb-3">
          <InlineAlert tone="error">{error}</InlineAlert>
        </div>
      )}

      <DataTable
        columns={columns}
        rows={modules.data?.content ?? []}
        rowKey={(module) => module.id}
        loading={modules.isPending}
        page={page}
        pageSize={pageSize}
        totalElements={modules.data?.totalElements ?? 0}
        onPageChange={(next) => setFilters({ page: String(next) })}
        onPageSizeChange={(next) => setFilters({ size: String(next), page: "0" })}
        onRowClick={can("MODULE_MANAGE") ? setEditing : undefined}
        /* A filter that matches nothing is not an empty catalogue, and saying so would send
           somebody off to create a module that already exists. */
        emptyTitle={narrowed ? "Nothing matches those filters" : "No modules yet"}
        emptyMessage={
          narrowed
            ? "No module here has that name or code, or that kind and status."
            : "Add the services your products are built from — an inventory engine, an accounting service — and link them to the projects that use them."
        }
      />

      <ModuleDrawer
        module={editing}
        open={adding || editing != null}
        onClose={() => {
          setAdding(false);
          setEditing(null);
        }}
        onSaved={refresh}
      />
      <ModuleReachDrawer module={reachOf} onClose={() => setReachOf(null)} />
    </>
  );
}

/**
 * Every project holding a shared module, and which version each is on.
 *
 * <p>This is the reach of a component release. A team about to ship Accounting 2.2 needs to know
 * that four other customers are on 2.1 and one is still on 2.0 — that is the blast radius, and it
 * is invisible from the release itself.
 */
function ModuleReachDrawer({
  module,
  onClose,
}: {
  module: ModuleView | null;
  onClose: () => void;
}) {
  const reach = useQuery({
    queryKey: ["module-reach", module?.id],
    queryFn: () => api.get<ModuleReach[]>(`/api/v1/modules/${module?.id}/projects`),
    enabled: module != null,
  });

  const rows = reach.data ?? [];
  const behind = rows.filter((row) => !row.onLatest);

  return (
    <Drawer
      open={module != null}
      onClose={onClose}
      title="Where it is used"
      subtitle={
        module
          ? `${module.name}${module.latestVersion ? ` · latest release ${module.latestVersion}` : ""}`
          : undefined
      }
    >
      <div className="space-y-3">
        {behind.length > 0 && (
          <InlineAlert tone="warning">
            {behind.length === 1
              ? "One project is not on the latest release."
              : `${behind.length} projects are not on the latest release.`}{" "}
            Releasing this module does not deploy it for them — each moves when it actually goes
            out there.
          </InlineAlert>
        )}

        {reach.isPending ? (
          <LoadingState label="Loading projects…" />
        ) : rows.length === 0 ? (
          <EmptyState
            title="Not used anywhere yet"
            message="Link this module to a project from that project's Modules tab."
          />
        ) : (
          <ul className="divide-y divide-ink-100 rounded-lg border border-ink-200">
            {rows.map((row) => (
              <li
                key={row.projectId}
                className="flex items-center justify-between gap-3 px-3 py-2.5"
              >
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-ink-900">{row.projectName}</p>
                  <p className="font-mono text-xs text-ink-400">{row.projectKey}</p>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <span className="tnum font-mono text-sm text-ink-700">
                    {row.currentVersion ?? "—"}
                  </span>
                  <StatusBadge tone={row.onLatest ? "green" : "amber"}>
                    {row.onLatest ? "Current" : "Behind"}
                  </StatusBadge>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Drawer>
  );
}
